import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/best-sellers", label: "Best Sellers" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-3 md:py-4"
      }`}
    >
      <div className={`mx-auto max-w-7xl px-4 md:px-8`}>
        <div
          className={`glass rounded-full px-4 md:px-6 py-2.5 flex items-center justify-between transition-all duration-500 ${
            scrolled ? "shadow-[0_10px_40px_-15px_rgba(244,187,41,0.25)]" : ""
          }`}
        >
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_taste-egg-street/artifacts/va7h5bvn_image.png"
              alt="Taste of Egg"
              className="h-14 md:h-16 w-auto rounded-md"
            />
            <span className="hidden sm:block leading-none">
              <span className="text-yellow-brand font-script text-xl md:text-2xl block leading-none">The Protein House</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                    isActive ? "text-yellow-brand" : "text-white/80 hover:text-yellow-brand"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="tel:0434508712"
              data-testid="navbar-call-cta"
              className="hidden md:inline-flex btn-primary !py-2 !px-4 text-sm"
            >
              <Phone className="w-4 h-4" /> Call Now
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              className="lg:hidden w-10 h-10 rounded-full glass flex items-center justify-center text-white"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-0 top-0 z-40 transition-all duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(24px)" }}
      >
        <div className="pt-28 px-6 flex flex-col gap-2">
          {links.map((l, idx) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-3xl font-script transition-colors ${
                  isActive ? "text-yellow-brand" : "text-white"
                }`
              }
              style={{ animation: open ? `fadeUp 0.5s ${idx * 0.05}s both` : undefined }}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-6 flex flex-col gap-3">
            <a href="tel:0434508712" className="btn-primary justify-center" data-testid="mobile-call-cta">
              <Phone className="w-4 h-4" /> Call 0434 508 712
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
